import { apiClient } from '@/lib/api-client';
import { Calendario, CalendarioEvento, CalendarioAcesso, ApiResponse } from '@/types';

type EventoParticipantePayload = {
  usuario_id?: string;
  nome?: string;
  email?: string;
  papel: 'responsavel_principal' | 'convidado';
};

/** Formato aceito pelo backend ao criar/editar um evento — distinto do modelo de leitura `CalendarioEvento`. */
export interface CalendarioEventoPayload {
  titulo: string;
  descricao?: string;
  local?: string;
  categoria?: string;
  status?: 'confirmado' | 'pendente' | 'cancelado';
  prioridade?: 'baixa' | 'media' | 'alta';
  lembrete_ativo?: boolean;
  lembrete_minutos_antes?: number;
  lembretes?: number[];
  recorrencia_rrule?: string | null;
  participantes?: EventoParticipantePayload[];
  data_inicio: string;
  data_fim?: string;
  dia_inteiro?: boolean;
  fornecedor?: string;
  peso_veiculo?: number | null;
  volume?: number | null;
  numero_nfe?: string;
  calendario_id?: string;
}

export interface GoogleCalendarStatus {
  configurado: boolean;
  conectado: boolean;
  google_email: string | null;
  sincronizacao_ativa: boolean;
  ultima_push_em: string | null;
  ultima_pull_em: string | null;
  calendarios_sincronizados: string[];
}

/**
 * Conflito de agenda de um participante. Itens de OUTRA empresa chegam anonimizados
 * (`oculto: true`, título genérico, sem calendário) — nunca mostrar detalhes deles.
 */
export interface ConflitoEvento {
  id?: string;
  titulo?: string | null;
  data_inicio: string;
  data_fim?: string | null;
  oculto?: boolean;
  calendario_id?: string | null;
}

export interface ConflitoParticipante {
  usuario_id: string;
  eventos: ConflitoEvento[];
}

export const calendarioService = {
  /**
   * `empresaId` mantém o comportamento antigo (calendários de uma empresa — CRM, superadmin).
   * `opcoes.escopo = 'pessoa'` traz os calendários de todas as empresas da pessoa (agenda única),
   * opcionalmente restritos por `empresaIds`.
   */
  async getAll(
    empresaId?: string,
    opcoes: { escopo?: 'pessoa'; empresaIds?: string[] } = {},
  ): Promise<ApiResponse<Calendario[]>> {
    try {
      const query = new URLSearchParams();
      if (empresaId) query.append('empresa_id', empresaId);
      if (opcoes.escopo) query.append('escopo', opcoes.escopo);
      if (opcoes.empresaIds?.length) query.append('empresa_ids', opcoes.empresaIds.join(','));
      const qs = query.toString() ? `?${query.toString()}` : '';
      return await apiClient.get<ApiResponse<Calendario[]>>(`/calendarios${qs}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar calendários', data: [] };
    }
  },

  async getById(id: string): Promise<ApiResponse<Calendario>> {
    try {
      return await apiClient.get<ApiResponse<Calendario>>(`/calendarios/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar calendário', data: {} as Calendario };
    }
  },

  async create(data: { nome: string; cor: string; descricao?: string; tipo?: 'padrao' | 'entregas'; empresa_id?: string }): Promise<ApiResponse<Calendario>> {
    try {
      return await apiClient.post<ApiResponse<Calendario>>('/calendarios', data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar calendário', data: {} as Calendario };
    }
  },

  async update(id: string, data: Partial<Calendario>): Promise<ApiResponse<Calendario>> {
    try {
      return await apiClient.put<ApiResponse<Calendario>>(`/calendarios/${id}`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar calendário', data: {} as Calendario };
    }
  },

  async remove(id: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/calendarios/${id}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir calendário', data: false };
    }
  },

  async getAcessos(id: string): Promise<ApiResponse<CalendarioAcesso[]>> {
    try {
      return await apiClient.get<ApiResponse<CalendarioAcesso[]>>(`/calendarios/${id}/acessos`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar acessos', data: [] };
    }
  },

  async addAcesso(
    id: string,
    data: { usuario_id: string; pode_editar: boolean },
  ): Promise<ApiResponse<CalendarioAcesso>> {
    try {
      return await apiClient.post<ApiResponse<CalendarioAcesso>>(`/calendarios/${id}/acessos`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao conceder acesso', data: {} as CalendarioAcesso };
    }
  },

  async removeAcesso(calendarioId: string, usuarioId: string): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.delete<ApiResponse<boolean>>(`/calendarios/${calendarioId}/acessos/${usuarioId}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao revogar acesso', data: false };
    }
  },

  /**
   * Empresas que entram na agenda única da pessoa (vínculo ativo, empresa ativa, módulo Calendário). Fonte do filtro
   * de empresas da tela — a lista de empresas da sessão pode trazer empresa inativa ou sem o módulo.
   */
  async getEmpresasDaAgenda(): Promise<ApiResponse<Array<{ empresa_id: string; nome: string; papel: 'admin' | 'comum' }>>> {
    try {
      return await apiClient.get<ApiResponse<Array<{ empresa_id: string; nome: string; papel: 'admin' | 'comum' }>>>(
        '/calendarios/empresas-da-agenda',
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao listar empresas da agenda', data: [] };
    }
  },

  async getFeedUrl(): Promise<ApiResponse<{ url: string }>> {
    try {
      return await apiClient.get<ApiResponse<{ url: string }>>('/calendarios/feed/token');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao obter URL de assinatura', data: { url: '' } };
    }
  },

  async regenerateFeedUrl(): Promise<ApiResponse<{ url: string }>> {
    try {
      return await apiClient.post<ApiResponse<{ url: string }>>('/calendarios/feed/token/regenerar');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao gerar nova URL de assinatura', data: { url: '' } };
    }
  },

  async getGoogleStatus(): Promise<ApiResponse<GoogleCalendarStatus>> {
    try {
      return await apiClient.get<ApiResponse<GoogleCalendarStatus>>('/calendarios/google/status');
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao obter status da integração com Google Calendar',
        data: {
          configurado: false,
          conectado: false,
          google_email: null,
          sincronizacao_ativa: false,
          ultima_push_em: null,
          ultima_pull_em: null,
          calendarios_sincronizados: [],
        },
      };
    }
  },

  async getGoogleConnectUrl(): Promise<ApiResponse<{ url: string }>> {
    try {
      return await apiClient.get<ApiResponse<{ url: string }>>('/calendarios/google/conectar');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao iniciar conexão com o Google Calendar', data: { url: '' } };
    }
  },

  async disconnectGoogle(): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.post<ApiResponse<boolean>>('/calendarios/google/desconectar');
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao desconectar do Google Calendar', data: false };
    }
  },

  async setGoogleSyncedCalendars(calendarioIds: string[]): Promise<ApiResponse<boolean>> {
    try {
      return await apiClient.put<ApiResponse<boolean>>('/calendarios/google/calendarios', { calendario_ids: calendarioIds });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao configurar calendários sincronizados', data: false };
    }
  },

  async syncGoogleNow(): Promise<ApiResponse<{ enviados: number; recebidos: number }>> {
    try {
      return await apiClient.post<ApiResponse<{ enviados: number; recebidos: number }>>('/calendarios/google/sincronizar-agora');
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao sincronizar com o Google Calendar',
        data: { enviados: 0, recebidos: 0 },
      };
    }
  },
};

export const calendarioEventoService = {
  async getEventos(params: {
    start?: string;
    end?: string;
    calendarioIds?: string[];
    empresa_id?: string;
    escopo?: 'pessoa';
    empresaIds?: string[];
  }): Promise<ApiResponse<CalendarioEvento[]>> {
    try {
      const query = new URLSearchParams();
      if (params.start) query.append('start', params.start);
      if (params.end) query.append('end', params.end);
      if (params.empresa_id) query.append('empresa_id', params.empresa_id);
      if (params.escopo) query.append('escopo', params.escopo);
      if (params.empresaIds?.length) query.append('empresa_ids', params.empresaIds.join(','));
      if (params.calendarioIds) {
        params.calendarioIds.forEach(id => query.append('calendarioIds', id));
      }
      const qs = query.toString();
      return await apiClient.get<ApiResponse<CalendarioEvento[]>>(`/calendarios/eventos/lista${qs ? `?${qs}` : ''}`);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao buscar eventos', data: [] };
    }
  },

  async create(
    calendarioId: string,
    data: CalendarioEventoPayload,
  ): Promise<ApiResponse<CalendarioEvento>> {
    try {
      return await apiClient.post<ApiResponse<CalendarioEvento>>(`/calendarios/${calendarioId}/eventos`, data);
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao criar evento', data: {} as CalendarioEvento };
    }
  },

  async update(
    calendarioId: string,
    eventoId: string,
    data: Partial<CalendarioEventoPayload>,
    options?: { scope?: 'single' | 'series' | 'future' },
  ): Promise<ApiResponse<CalendarioEvento>> {
    try {
      const query = new URLSearchParams();
      if (options?.scope) query.append('scope', options.scope);
      const qs = query.toString();
      return await apiClient.put<ApiResponse<CalendarioEvento>>(
        `/calendarios/${calendarioId}/eventos/${eventoId}${qs ? `?${qs}` : ''}`,
        data,
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao atualizar evento', data: {} as CalendarioEvento };
    }
  },

  async remove(
    calendarioId: string,
    eventoId: string,
    options?: { scope?: 'single' | 'series' | 'future' },
  ): Promise<ApiResponse<boolean>> {
    try {
      const query = new URLSearchParams();
      if (options?.scope) query.append('scope', options.scope);
      const qs = query.toString();
      return await apiClient.delete<ApiResponse<boolean>>(
        `/calendarios/${calendarioId}/eventos/${eventoId}${qs ? `?${qs}` : ''}`,
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao excluir evento', data: false };
    }
  },

  async exportIcs(params: {
    start?: string;
    end?: string;
    calendarioIds?: string[];
    empresa_id?: string;
    escopo?: 'pessoa';
    empresaIds?: string[];
  }): Promise<Blob> {
    const query = new URLSearchParams();
    if (params.start) query.append('start', params.start);
    if (params.end) query.append('end', params.end);
    if (params.empresa_id) query.append('empresa_id', params.empresa_id);
    if (params.escopo) query.append('escopo', params.escopo);
    if (params.empresaIds?.length) query.append('empresa_ids', params.empresaIds.join(','));
    if (params.calendarioIds) {
      params.calendarioIds.forEach(id => query.append('calendarioIds', id));
    }
    const qs = query.toString();
    return apiClient.getBlob(`/calendarios/eventos/export.ics${qs ? `?${qs}` : ''}`);
  },

  async restore(calendarioId: string, eventoId: string): Promise<ApiResponse<CalendarioEvento>> {
    try {
      return await apiClient.post<ApiResponse<CalendarioEvento>>(
        `/calendarios/${calendarioId}/eventos/${eventoId}/restaurar`,
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao restaurar evento', data: {} as CalendarioEvento };
    }
  },

  async respondRsvp(
    calendarioId: string,
    eventoId: string,
    resposta: 'aceito' | 'recusado' | 'talvez',
  ): Promise<ApiResponse<{ rsvp_status: string }>> {
    try {
      return await apiClient.post<ApiResponse<{ rsvp_status: string }>>(
        `/calendarios/${calendarioId}/eventos/${eventoId}/rsvp`,
        { resposta },
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao registrar RSVP', data: { rsvp_status: '' } };
    }
  },

  async getConflitos(params: {
    usuarioIds: string[];
    dataInicio: string;
    dataFim?: string;
    excluirEventoId?: string;
    /** Calendário de destino do evento — o backend usa a empresa dele pra decidir o que é "outra empresa". */
    calendarioId?: string;
  }): Promise<ApiResponse<ConflitoParticipante[]>> {
    try {
      const query = new URLSearchParams();
      params.usuarioIds.forEach((usuarioId) => query.append('usuario_ids', usuarioId));
      query.append('data_inicio', params.dataInicio);
      if (params.dataFim) query.append('data_fim', params.dataFim);
      if (params.excluirEventoId) query.append('excluir_evento_id', params.excluirEventoId);
      if (params.calendarioId) query.append('calendario_id', params.calendarioId);
      return await apiClient.get<ApiResponse<ConflitoParticipante[]>>(
        `/calendarios/participantes/conflitos?${query.toString()}`,
      );
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Erro ao verificar conflitos', data: [] };
    }
  },
};
